<script setup lang="ts">
import { computed, ref } from "vue";

/**
 * Renders a small operator-pasteable Jaeger trace_id reference under
 * an error message. Click-to-copy puts the full 32-char hex on the
 * clipboard; the displayed prefix is truncated to 12 chars per
 * ``docs/design/error-correlation-one-pager.md`` Decisions §D4.
 *
 * Rendering rule (Decisions §D6): the parent decides when to show
 * this component. UNAUTHENTICATED toasts skip it (the user's next
 * action is re-login, not report). Other error codes include it iff
 * ``traceId`` is defined.
 *
 * Renders nothing when ``traceId`` is undefined — safe to drop into
 * a template without v-if at the call site.
 */
const props = defineProps<{
  traceId?: string;
}>();

const DISPLAY_LEN = 12;

const displayId = computed(() => {
  if (!props.traceId) return "";
  return props.traceId.slice(0, DISPLAY_LEN);
});

const justCopied = ref(false);

async function copyToClipboard() {
  if (!props.traceId) return;
  try {
    // navigator.clipboard is HTTPS-only or localhost; in dev it works
    // out of the box. On unsupported browsers fall back silently —
    // the user can still read the prefix visually.
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(props.traceId);
      justCopied.value = true;
      setTimeout(() => {
        justCopied.value = false;
      }, 1200);
    }
  } catch {
    // Clipboard access denied (permissions policy, focus loss). Leave
    // the label as-is; the user can manually select-and-copy the
    // displayed prefix and the trace will still resolve in Jaeger
    // (prefix search is supported).
  }
}
</script>

<template>
  <button
    v-if="traceId"
    type="button"
    data-testid="error-trace-ref"
    :title="`Click to copy full ID: ${traceId}`"
    class="mt-1 inline-flex items-center gap-1 font-mono text-[11px] leading-none text-text-tertiary hover:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent rounded transition-colors"
    @click="copyToClipboard"
  >
    <span v-if="justCopied" data-testid="error-trace-ref-copied">
      Copied!
    </span>
    <span v-else>
      Reference: {{ displayId }}…
    </span>
  </button>
</template>
